import { useEffect, useMemo } from 'react';

import { useAppDispatch, useAppSelector } from '@src/core/hooks';
import { useWidgetId } from '@src/core/IdProvider';
import useSelectedProductId from '@src/hooks/useSelectedProductId';
import {
  loadingTariffVerificationsSelector,
  loadTariffVerifications,
  tariffVerificationValuesSelector,
  tariffVerificationsSelector,
  updateTariffVerificationValues,
} from '@src/stores/product';
import { addCartItemAddedSelector } from '@src/features/cart/store/cart';

import { useTariffVerificationHandlers } from '@src/domains/products/features/productAdder/hooks/useTariffVerificationHandlers';
import { cleanExcessVerificationValues } from '@src/domains/tariffs/core/tariffVerificationFields/cleanExcessVerificationValues';
import { filterRenderableVerifications } from '@src/domains/tariffs/core/tariffVerificationFields/filterRenderableVerifications';

type UseVerificationFieldsParams = {
  tariffTypeId: string;
  quantity: number;
};

export function useVerificationFields({ tariffTypeId, quantity }: UseVerificationFieldsParams) {
  const widgetId = useWidgetId();
  const productId = useSelectedProductId();
  const dispatch = useAppDispatch();

  const tariffTypeVerifications = useAppSelector(tariffVerificationsSelector(tariffTypeId));
  const loading = useAppSelector(loadingTariffVerificationsSelector(tariffTypeId));
  const tariffWithVerificationValues = useAppSelector(tariffVerificationValuesSelector(widgetId, productId));
  const { errors: cartErrors } = useAppSelector(addCartItemAddedSelector);

  const arrayOfQuantity = useMemo(() => Array.from({ length: quantity }), [quantity]);

  const verificationValuesWithoutUnrendered = useMemo(
    () => filterRenderableVerifications(tariffTypeVerifications),
    [tariffTypeVerifications],
  );

  const tariffHasValidationsToRender = verificationValuesWithoutUnrendered.length > 0;

  useEffect(() => {
    if (quantity > 0 && tariffTypeId) {
      dispatch(loadTariffVerifications(tariffTypeId));
    }
  }, [tariffTypeId, quantity, dispatch]);

  useEffect(() => {
    if (tariffWithVerificationValues && tariffTypeId) {
      const { updatePayload } = cleanExcessVerificationValues(tariffWithVerificationValues, tariffTypeId, quantity);
      if (updatePayload) {
        dispatch(updateTariffVerificationValues(widgetId, productId, updatePayload));
      }
    }
  }, [quantity, tariffWithVerificationValues, tariffTypeId, dispatch, widgetId, productId]);

  const { handleValueChange } = useTariffVerificationHandlers(tariffTypeId);

  return {
    loading,
    tariffHasValidationsToRender,
    arrayOfQuantity,
    verificationValuesWithoutUnrendered,
    tariffWithVerificationValues,
    cartErrors,
    handleValueChange,
  };
}
